import { useState } from 'react'
import { Button } from '@journ/ui/button'
import { Icons } from '@journ/ui/icons'
import { Popover, PopoverContent, PopoverTrigger } from '@journ/ui/popover'
import { ScrollArea } from '@journ/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@journ/ui/tabs'
import { useSelector } from '@/libs/state'
import { useIntlayer } from 'react-intlayer'
import { formatDistanceToNow } from 'date-fns'
import { tv } from '@journ/ui/cn'
import {
	notifications$,
	markNotificationRead,
	markNotificationArchived,
	markAllNotificationsRead,
	archiveAllNotifications,
} from '../index'
import type { Notification } from '../domain/notification'

const levelIcon = (n: Notification) => {
	if (n.level === 'success') return <Icons.Check className='size-4 text-emerald-500' />
	if (n.level === 'error') return <Icons.Error className='size-4 text-destructive' />
	if (n.level === 'warn') return <Icons.AlertCircle className='size-4 text-amber-500' />
	return <Icons.AlertCircle className='size-4 text-606060' />
}

type NotificationRowProps = {
	notification: Notification
	onArchive?: (id: string) => void
}

const NotificationRow = ({ notification, onArchive }: NotificationRowProps) => {
	const isUnread = notification.status === 'unread'

	const handleClick = () => {
		if (isUnread) markNotificationRead(notification.id)
	}

	return (
		<div
			role='button'
			tabIndex={0}
			onClick={handleClick}
			onKeyDown={e => e.key === 'Enter' && handleClick()}
			className={tv({
				base: 'flex items-start gap-3 px-4 py-3 border-b border-border last:border-0 cursor-default transition-colors hover:bg-muted/40',
			})()}
		>
			<div className='mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border border-border bg-background'>
				{levelIcon(notification)}
			</div>
			<div className='min-w-0 flex-1'>
				{notification.title && (
					<p className={tv({ base: 'text-sm leading-snug' })({ class: isUnread ? 'font-medium' : '' })}>
						{notification.title}
					</p>
				)}
				<p
					className={tv({ base: 'text-sm leading-snug text-606060' })({
						class: !notification.title && isUnread ? 'font-medium text-foreground' : '',
					})}
				>
					{notification.message}
				</p>
				<p className='mt-1 text-xxs text-878787'>
					{formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
				</p>
			</div>
			{onArchive && (
				<Button
					size='icon'
					variant='ghost'
					className='size-7 shrink-0 text-606060 hover:text-foreground'
					onClick={e => {
						e.stopPropagation()
						onArchive(notification.id)
					}}
					title='Archive'
				>
					<Icons.Inventory2 className='size-4' />
				</Button>
			)}
		</div>
	)
}

const EmptyState = ({ label }: { label: string }) => (
	<div className='flex h-40 items-center justify-center text-xs text-606060'>{label}</div>
)

const NotificationCenter = () => {
	const [open, setOpen] = useState(false)
	const c = useIntlayer('notification-center')
	const all = useSelector(notifications$)

	const inbox = all.filter(n => n.status !== 'archived')
	const archived = all.filter(n => n.status === 'archived')
	const hasUnread = inbox.some(n => n.status === 'unread')

	const handleOpenChange = (next: boolean) => {
		setOpen(next)
		if (next && hasUnread) markAllNotificationsRead()
	}

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button variant='outline' size='icon' className='relative size-8 rounded-full'>
					{hasUnread && <span className='absolute right-0 top-0 size-2 rounded-full bg-amber-400' />}
					<Icons.Notifications size={16} />
				</Button>
			</PopoverTrigger>
			<PopoverContent className='w-95 p-0 overflow-hidden' align='end' sideOffset={10}>
				<Tabs defaultValue='inbox'>
					<TabsList className='w-full justify-start rounded-none border-b bg-transparent px-2 py-5'>
						<TabsTrigger value='inbox' className='font-normal'>
							{c.inbox}
						</TabsTrigger>
						<TabsTrigger value='archive' className='font-normal'>
							{c.archive}
						</TabsTrigger>
					</TabsList>

					<TabsContent value='inbox' className='relative mt-0'>
						{inbox.length === 0 ? (
							<EmptyState label={c.emptyInbox.value} />
						) : (
							<>
								<ScrollArea className='max-h-110'>
									{inbox.map(n => (
										<NotificationRow key={n.id} notification={n} onArchive={markNotificationArchived} />
									))}
								</ScrollArea>
								<div className='flex items-center justify-center border-t border-border py-2'>
									<Button variant='ghost' size='sm' className='text-xs text-606060' onClick={archiveAllNotifications}>
										{c.archiveAll}
									</Button>
								</div>
							</>
						)}
					</TabsContent>

					<TabsContent value='archive' className='mt-0'>
						{archived.length === 0 ? (
							<EmptyState label={c.emptyArchive.value} />
						) : (
							<ScrollArea className='max-h-124'>
								{archived.map(n => (
									<NotificationRow key={n.id} notification={n} />
								))}
							</ScrollArea>
						)}
					</TabsContent>
				</Tabs>
			</PopoverContent>
		</Popover>
	)
}

export { NotificationCenter }
